import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { inventoryAPI } from '../../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const KitBuildForm = ({ formik }) => {
  const [showAddItem, setShowAddItem] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Initial item state
  const initialItemState = {
    productSku: '',
    productName: '',
    quantity: 1,
    notes: ''
  };
  
  const [currentItem, setCurrentItem] = useState({ ...initialItemState });
  
  // For labor charges
  const [showAddLabor, setShowAddLabor] = useState(false);
  const [currentLabor, setCurrentLabor] = useState({
    description: '',
    hours: 1,
    ratePerHour: 50
  });

  // Fetch product suggestions when user types in search
  const { data: productSuggestions, isLoading } = useQuery(
    ['productSuggestions', searchTerm],
    () => inventoryAPI.getInventory({ search: searchTerm, limit: 5, customerId: formik.values.customerId }),
    {
      enabled: searchTerm.length > 2,
      keepPreviousData: true
    }
  );
  
  // Handle date change
  const handleDateChange = (date) => {
    formik.setFieldValue('scheduledDate', date);
  };
  
  // Handle product selection
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setCurrentItem({
      ...currentItem,
      productSku: product.productSku,
      productName: product.productName
    });
    setSearchTerm('');
  };
  
  // Handle adding item to the request
  const handleAddItem = () => {
    if (!currentItem.productSku || !currentItem.productName || currentItem.quantity <= 0) {
      return;
    }
    
    // Add the current item to the line items array
    formik.setFieldValue('lineItems', [
      ...formik.values.lineItems,
      { ...currentItem, type: 'item' }
    ]);
    
    // Reset current item
    setCurrentItem({ ...initialItemState });
    setSelectedProduct(null);
    setShowAddItem(false);
  };
  
  // Handle removing item from the request
  const handleRemoveItem = (index) => {
    const updatedItems = [...formik.values.lineItems];
    updatedItems.splice(index, 1);
    formik.setFieldValue('lineItems', updatedItems);
  };
  
  // Handle adding labor to the request
  const handleAddLabor = () => {
    if (!currentLabor.description || currentLabor.hours <= 0 || currentLabor.ratePerHour <= 0) {
      return;
    }
    
    // Calculate total cost
    const laborCost = currentLabor.hours * currentLabor.ratePerHour;
    
    // Add the labor to the line items array
    formik.setFieldValue('lineItems', [
      ...formik.values.lineItems,
      { 
        ...currentLabor, 
        type: 'labor',
        totalCost: laborCost
      }
    ]);
    
    // Reset current labor
    setCurrentLabor({
      description: '',
      hours: 1,
      ratePerHour: 50
    });
    setShowAddLabor(false);
  };
  
  // Calculate total cost of the kit
  const calculateTotalCost = () => {
    return formik.values.lineItems.reduce((total, item) => {
      if (item.type === 'labor') {
        return total + (item.hours * item.ratePerHour);
      }
      return total;
    }, 0);
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Kit Building Request Details</h3>
      
      {/* Scheduled Date */}
      <div>
        <label htmlFor="scheduledDate" className="block text-sm font-medium text-gray-700">
          Requested Completion Date
        </label>
        <div className="mt-1">
          <DatePicker
            selected={formik.values.scheduledDate ? new Date(formik.values.scheduledDate) : null}
            onChange={handleDateChange}
            minDate={new Date()}
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            placeholderText="Select date"
          />
        </div>
        {formik.touched.scheduledDate && formik.errors.scheduledDate && (
          <p className="mt-2 text-sm text-red-600">{formik.errors.scheduledDate}</p>
        )}
      </div>
      
      {/* Kit Details */}
      <div>
        <label htmlFor="kitName" className="block text-sm font-medium text-gray-700">
          Kit Name
        </label>
        <input
          type="text"
          value={formik.values.kitName || ''}
          onChange={(e) => formik.setFieldValue('kitName', e.target.value)}
          className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
          placeholder="e.g., AV Equipment Bundle, Server Rack Kit"
        />
      </div>
      
      {/* Kit Quantity */}
      <div>
        <label htmlFor="kitQuantity" className="block text-sm font-medium text-gray-700">
          Number of Kits to Build
        </label>
        <input
          type="number"
          min="1"
          value={formik.values.kitQuantity || 1}
          onChange={(e) => formik.setFieldValue('kitQuantity', parseInt(e.target.value) || 1)}
          className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
        />
      </div>
      
      {/* Line Items */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-base font-medium text-gray-900">Kit Components</h4>
          <div className="space-x-2">
            <button
              type="button"
              onClick={() => setShowAddItem(true)}
              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Add Product
            </button>
            <button
              type="button"
              onClick={() => setShowAddLabor(true)}
              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Add Labor
            </button>
          </div>
        </div>
        
        {formik.values.lineItems?.length === 0 ? (
          <div className="mt-2 text-sm text-gray-500 bg-gray-50 p-4 rounded-md">
            No items added yet. Add products and labor to build your kit.
          </div>
        ) : (
          <div className="mt-2 border border-gray-200 rounded-md divide-y divide-gray-200">
            {formik.values.lineItems?.map((item, index) => (
              <div key={index} className="p-4 flex justify-between items-start">
                <div>
                  {item.type === 'item' ? (
                    <>
                      <p className="font-medium">{item.productName}</p>
                      <p className="text-sm text-gray-500">SKU: {item.productSku}</p>
                      <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium">Labor: {item.description}</p>
                      <p className="text-sm text-gray-500">
                        {item.hours} hours @ ${item.ratePerHour}/hr = ${item.totalCost}
                      </p>
                    </>
                  )}
                  
                  {item.notes && (
                    <p className="text-sm text-gray-500 mt-1">Notes: {item.notes}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="text-red-600 hover:text-red-900"
                >
                  Remove
                </button>
              </div>
            ))}
            
            {/* Total Labor Cost */}
            {formik.values.lineItems?.some(item => item.type === 'labor') && (
              <div className="p-4 bg-gray-50">
                <p className="font-medium">Total Labor Cost: ${calculateTotalCost()}</p>
              </div>
            )}
          </div>
        )}
        
        {formik.touched.lineItems && formik.errors.lineItems && (
          <p className="mt-2 text-sm text-red-600">{formik.errors.lineItems}</p>
        )}
      </div>
      
      {/* Add Item Form */}
      {showAddItem && (
        <div className="mt-4 bg-gray-50 p-4 rounded-md space-y-4">
          <h4 className="text-base font-medium text-gray-900">Add Product to Kit</h4>
          
          {/* Product Search */}
          <div>
            <label htmlFor="productSearch" className="block text-sm font-medium text-gray-700">
              Search Your Inventory
            </label>
            <div className="mt-1 relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Type to search products..."
              />
              {searchTerm.length > 2 && productSuggestions && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                  {isLoading ? (
                    <div className="p-2 text-center text-gray-500">Loading...</div>
                  ) : productSuggestions.data.length > 0 ? (
                    productSuggestions.data.map((product) => (
                      <div
                        key={product.id}
                        className="p-2 hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleProductSelect(product)}
                      >
                        <p className="font-medium">{product.productName}</p>
                        <p className="text-sm text-gray-500">SKU: {product.productSku}</p>
                        <p className="text-xs text-gray-500">Available: {product.quantity}</p>
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-center text-gray-500">
                      No products found. You can manually enter details below.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Product Details */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="productSku" className="block text-sm font-medium text-gray-700">
                Product SKU
              </label>
              <input
                type="text"
                value={currentItem.productSku}
                onChange={(e) => setCurrentItem({...currentItem, productSku: e.target.value})}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div>
              <label htmlFor="productName" className="block text-sm font-medium text-gray-700">
                Product Name
              </label>
              <input
                type="text"
                value={currentItem.productName}
                onChange={(e) => setCurrentItem({...currentItem, productName: e.target.value})}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                Quantity Per Kit
              </label>
              <input
                type="number"
                min="1"
                value={currentItem.quantity}
                onChange={(e) => setCurrentItem({...currentItem, quantity: parseInt(e.target.value) || 0})}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>
          
          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
              Assembly Notes (Optional)
            </label>
            <textarea
              rows="2"
              value={currentItem.notes}
              onChange={(e) => setCurrentItem({...currentItem, notes: e.target.value})}
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              placeholder="e.g., Install in front bay of rack, Connect to port 2"
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowAddItem(false);
                setCurrentItem({...initialItemState});
              }}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
              disabled={!currentItem.productSku || !currentItem.productName || currentItem.quantity <= 0}
            >
              Add to Kit
            </button>
          </div>
        </div>
      )}
      
      {/* Add Labor Form */}
      {showAddLabor && (
        <div className="mt-4 bg-gray-50 p-4 rounded-md space-y-4">
          <h4 className="text-base font-medium text-gray-900">Add Labor Service</h4>
          
          <div>
            <label htmlFor="laborDescription" className="block text-sm font-medium text-gray-700">
              Service Description
            </label>
            <input
              type="text"
              value={currentLabor.description}
              onChange={(e) => setCurrentLabor({...currentLabor, description: e.target.value})}
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              placeholder="e.g., Assembly, Configuration, Testing"
            />
          </div>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="hours" className="block text-sm font-medium text-gray-700">
                Estimated Hours
              </label>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={currentLabor.hours}
                onChange={(e) => setCurrentLabor({...currentLabor, hours: parseFloat(e.target.value) || 0})}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div>
              <label htmlFor="ratePerHour" className="block text-sm font-medium text-gray-700">
                Rate Per Hour ($)
              </label>
              <input
                type="number"
                min="1"
                value={currentLabor.ratePerHour}
                onChange={(e) => setCurrentLabor({...currentLabor, ratePerHour: parseFloat(e.target.value) || 0})}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>
          
          <div className="py-2 px-3 bg-gray-100 rounded-md">
            <p className="text-sm font-medium">
              Total Cost: ${(currentLabor.hours * currentLabor.ratePerHour).toFixed(2)}
            </p>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowAddLabor(false);
                setCurrentLabor({
                  description: '',
                  hours: 1,
                  ratePerHour: 50
                });
              }}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddLabor}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
              disabled={!currentLabor.description || currentLabor.hours <= 0 || currentLabor.ratePerHour <= 0}
            >
              Add Labor
            </button>
          </div>
        </div>
      )}
      
      {/* Special Instructions */}
      <div>
        <label htmlFor="specialInstructions" className="block text-sm font-medium text-gray-700">
          Special Instructions (Optional)
        </label>
        <textarea
          rows="3"
          value={formik.values.specialInstructions || ''}
          onChange={(e) => formik.setFieldValue('specialInstructions', e.target.value)}
          className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
        ></textarea>
      </div>
      
      {/* Assembly Checklist */}
      <div>
        <h4 className="text-base font-medium text-gray-900 mb-2">Quality Control Checklist</h4>
        <div className="space-y-2">
          {formik.values.qcChecklist?.map((item, index) => (
            <div key={index} className="flex items-start">
              <div className="h-5 flex items-center">
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={(e) => {
                    const updatedChecklist = [...formik.values.qcChecklist];
                    updatedChecklist[index].checked = e.target.checked;
                    formik.setFieldValue('qcChecklist', updatedChecklist);
                  }}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => {
                    const updatedChecklist = [...formik.values.qcChecklist];
                    updatedChecklist[index].description = e.target.value;
                    formik.setFieldValue('qcChecklist', updatedChecklist);
                  }}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="QC step description"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const updatedChecklist = [...formik.values.qcChecklist];
                  updatedChecklist.splice(index, 1);
                  formik.setFieldValue('qcChecklist', updatedChecklist);
                }}
                className="ml-2 text-red-600 hover:text-red-900"
              >
                <span className="sr-only">Remove</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
          
          <button
            type="button"
            onClick={() => {
              const currentChecklist = formik.values.qcChecklist || [];
              formik.setFieldValue('qcChecklist', [
                ...currentChecklist,
                { description: '', checked: false }
              ]);
            }}
            className="mt-2 inline-flex items-center px-2 py-1 border border-transparent text-sm font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
          >
            <svg className="-ml-1 mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add QC Checklist Item
          </button>
        </div>
      </div>
    </div>
  );
};

export default KitBuildForm; 